import React, { useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Container,
  Alert,
  Snackbar,
  Typography,
  Divider,
} from '@mui/material';
import { BetaAcceptanceDialog } from '../common/components/layout/BetaAcceptanceDialog';
import { useGetProjectsQuery } from '../generated/graphql';
import BatchProjectUploadDialog from '../common/components/_misc/batchProjectUploadDialog/BatchProjectUploadDialog';
import UploadTypeSelectionDialog from '../common/components/_misc/uploadTypeSelectionDialog/UploadTypeSelectionDialog';
import {
  ProjectsProvider,
  useProjectsContext,
  ProjectsHeader,
  ProjectsGrid,
  ProjectsList,
  EmptyState,
  useBatchUploadHandler,
} from '../common/components/projectFiles';
import { LoadingState } from '../common/components/loading';
import { FadeContent } from '../common/components/reactBits';
import { useModeTokens } from '../common/components/ui/useModeTokens';
import { useLocation, useNavigate } from 'react-router-dom';
import type { DawSaveAction } from '../types/electron';

const ProjectsContent: React.FC = () => {
  const t = useModeTokens();
  const location = useLocation();
  const navigate = useNavigate();
  const handledSaveAction = useRef<number | null>(null);
  const {
    uploadTypeDialogOpen,
    setUploadTypeDialogOpen,
    batchUploadDialogOpen,
    setBatchUploadDialogOpen,
    detectedProjects,
    setDetectedProjects,
    setUploadFiles,
    isBatchUploading,
    batchUploadProgress,
    isScanning,
    currentScanningFile,
  } = useProjectsContext();

  const {
    data: projectsData,
    loading: projectsLoading,
    error: projectsError,
    refetch: refetchProjects,
  } = useGetProjectsQuery({
    fetchPolicy: 'cache-and-network',
    // Poll every 5 minutes to keep project list up-to-date
    pollInterval: 300000, // 5 minutes in milliseconds
    notifyOnNetworkStatusChange: true,
  });

  const {
    handleBatchUpload,
    handleProjectFilesFolderSelect,
    handleDetectedProjectPath,
    handleStemsFolderSelect,
  } = useBatchUploadHandler({
    onProjectsRefetch: refetchProjects,
  });

  useEffect(() => {
    const routeState = location.state as {
      dawSaveAction?: DawSaveAction;
      receivedAt?: number;
    } | null;
    const action = routeState?.dawSaveAction;
    const receivedAt = routeState?.receivedAt;
    if (!action || !receivedAt || handledSaveAction.current === receivedAt)
      return;
    handledSaveAction.current = receivedAt;

    if (action.kind === 'upload') {
      void handleDetectedProjectPath(
        action.projectPath,
        action.projectFilePath
      );
    } else if (action.projectId && action.commitMessage) {
      navigate(`/projects/${encodeURIComponent(action.projectId)}`, {
        replace: true,
        state: { ...routeState, from: '/projects' },
      });
      return;
    }
    navigate('/projects', { replace: true, state: null });
  }, [handleDetectedProjectPath, location.state, navigate]);

  const allProjects = useMemo(
    () => projectsData?.projects || [],
    [projectsData?.projects]
  );

  useEffect(() => {
    window.electronAPI?.setDawSaveProjectIds?.(
      allProjects.map((project) => project._id)
    );
  }, [allProjects]);

  const sortedProjects = useMemo(
    () =>
      [...allProjects].sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      ),
    [allProjects]
  );

  const recentProjects = useMemo(
    () => sortedProjects.slice(0, 5),
    [sortedProjects]
  );

  if (projectsLoading && !projectsData) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <LoadingState message="Loading projects..." minHeight={400} />
      </Container>
    );
  }

  if (projectsError) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          Failed to load projects. Please try again later.
        </Alert>
      </Container>
    );
  }

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{
        py: { xs: 1.5, sm: 1 },
        px: { xs: 0, sm: 2 },
      }}
    >
      <FadeContent duration={560}>
        <ProjectsHeader
          onRefresh={refetchProjects}
          loading={projectsLoading}
          projectCount={allProjects.length}
        />
      </FadeContent>

      {allProjects.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          {/* Recent Projects Section */}
          <Box sx={{ mb: 3 }}>
            <ProjectsGrid projects={recentProjects} />
          </Box>

          <Divider sx={{ mb: 3, borderColor: t.borders.divider }} />

          {/* All Projects Table */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              mb: 1.5,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                color: t.text.bright,
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              All Projects
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                opacity: 0.55,
                fontSize: '0.7rem',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              Click any row to open
            </Typography>
          </Box>

          <Box
            sx={{
              width: { xs: 'calc(100% + 24px)', sm: '100%' },
              mx: { xs: -1.5, sm: 0 },
              overflow: 'hidden',
            }}
          >
            <ProjectsList
              projects={sortedProjects}
              onProjectSelect={(projectId) =>
                navigate(`/projects/${encodeURIComponent(projectId)}`, {
                  state: { from: `${location.pathname}${location.search}` },
                })
              }
              onRefreshProjects={refetchProjects}
            />
          </Box>
        </>
      )}

      <UploadTypeSelectionDialog
        open={uploadTypeDialogOpen}
        onClose={() => setUploadTypeDialogOpen(false)}
        onSelectProjectFiles={handleProjectFilesFolderSelect}
        onSelectStems={handleStemsFolderSelect}
      />

      <BatchProjectUploadDialog
        open={batchUploadDialogOpen}
        onClose={() => {
          setBatchUploadDialogOpen(false);
          setDetectedProjects([]);
          setUploadFiles(null);
        }}
        detectedProjects={detectedProjects}
        existingProjects={allProjects.map((p) => ({
          _id: p._id,
          name: p.name,
        }))}
        onUpload={handleBatchUpload}
        isUploading={isBatchUploading}
        uploadProgress={batchUploadProgress}
      />

      <Snackbar
        open={isScanning && !!currentScanningFile}
        message={currentScanningFile || 'Scanning...'}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="info" sx={{ width: 500, height: 100 }}>
          Scanning: {currentScanningFile}
        </Alert>
      </Snackbar>
    </Container>
  );
};

const ProjectsPage: React.FC = () => {
  return (
    <ProjectsProvider>
      <BetaAcceptanceDialog />
      <ProjectsContent />
    </ProjectsProvider>
  );
};

export default ProjectsPage;
